/**
 * Route copy for `src/routes/approvals` (keys: `routes.approvals.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  approvalsInboxPage: {
    delete: 'Delete',
    deleteRequestTitle: 'Delete this approval request?',
    deleteRequestMessage: 'The "{{type}}" request will be permanently removed from the history. Only rejected, expired or cancelled requests can be deleted.',
    requestDeleted: 'Request deleted.',
    deletePolicyTitle: 'Delete this policy?',
    deletePolicyMessage: 'Your workspace policy for "{{action}}" will be deleted. The platform default will apply to this action type again.',
    deletePolicyLabel: 'Delete policy {{action}}',
    policyDeleted: 'Policy deleted.',
    tenant: 'tenant',
    pending: 'Pending',
    approved: 'Approved',
    dismissed: 'Dismissed',
    expired: 'Expired',
    cancelled: 'Cancelled',
    all: 'All',
    autoApprove: 'Auto-approve',
    manualReview: 'Manual review',
    block: 'Block',
    approvalsHil: 'Approvals (HIL)',
    humanReviewOfPendingAiOutput: 'Human review of pending AI output and approval policies per action type.',
    hideThePolicies: 'Hide the policies',
    policies: 'Policies',
    status: 'Status',
    entityType: 'Entity type',
    allTypes: 'All types',
    loadingApprovals: 'Loading approvals…',
    noApprovals: 'No approvals',
    nothingToReviewForThisFilter: 'Nothing to review for this filter. Pending AI output will appear here.',
    createdOnV: 'Created on {{v}}',
    expiresOnV: ' · expires on {{v}}',
    entity: 'Entity:',
    run: 'Run:',
    requestedBy: 'Requested by:',
    detailedPreview: '👁 Detailed preview',
    decision: ' & decision',
    draftDraftPayload: 'Draft (draft_payload)',
    diff: 'Diff',
    feedback: 'Feedback:',
    feedbackOptional: 'Feedback (optional)',
    commentPassedToTheProducer: 'Comment passed to the producer…',
    approve: 'Approve',
    reject: 'Reject',
    previewEntityType: 'Preview — {{entity_type}}',
    type: 'Type:',
    created: 'Created:',
    draftContentToApprove: 'Draft (content to approve)',
    changesDiff: 'Changes (diff)',
    approvalPolicies: 'Approval policies',
    effectiveModePerActionTypeTenant: 'Effective mode per action type (tenant overrides take precedence over platform defaults).',
    noPolicy: 'No policy',
    addARuleBelow: 'Add a rule below.',
    actionType: 'Action type',
    eGGeneratedContentCopilotAction: 'e.g. generated_content, copilot_action',
    mode: 'Mode',
    add: 'Add',
    platform: 'platform',
    entityAgentTask: 'AI agent task',
    entityMasToolCall: 'Tool action (AI agent)',
    entityGeneratedContent: 'Generated content',
    entityPlannedPost: 'Scheduled post',
    entityCopilotAction: 'Copilot action',
    entityCommunityReply: 'Community reply',
    entityCommunityInteraction: 'Community interaction',
    entityEmailReply: 'Email reply',
    entityImportBatch: 'Import batch',
    entityWorkflowStep: 'Workflow step',
    entityGeneric: 'Generic',
    taskLabel: 'Task:',
  },
  publicApprovalPage: {
    invalidApprovalLink: 'Invalid approval link.',
    couldNotLoadThisRevision: 'Could not load this revision.',
    thankYouYourApprovalHasBeen: 'Thank you — your approval has been recorded.',
    yourRefusalHasBeenRecorded: 'Your refusal has been recorded.',
    theActionFailedTryAgain: 'The action failed. Try again.',
    commentSent: 'Comment sent.',
    sendingTheCommentFailed: 'Sending the comment failed.',
    loadingTheRevision: 'Loading the revision…',
    invalidOrExpiredLink: 'Invalid or expired link',
    thisApprovalLinkIsNoLonger: 'This approval link is no longer valid. Ask the person who sent it for a new link.',
    couldNotDisplayThisRevision: 'Could not display this revision',
    youAreReviewingThisContentThrough: 'You are reviewing this content through a personal, secure link.',
    approvalRequest: 'Approval request',
    contentToReview: 'Content to review',
    helloName: 'Hello {{name}}, ',
    youAreReviewingTheStageName: 'you are reviewing the “{{stage_name}}” step.',
    yourOpinionIsRequested: 'your opinion is requested.',
    youHaveAlready: 'You have already',
    approved: 'approved',
    rejected: 'rejected',
    thisContent: 'this content.',
    decision: 'Decision',
    commentOptional: 'Comment (optional)',
    anyFeedbackForTheAuthor: 'Any feedback for the author?',
    approving: 'Approving…',
    approve: 'Approve',
    refusing: 'Refusing…',
    reject: 'Reject',
    comments: 'Comments',
    noCommentYet: 'No comment yet.',
    guest: 'Guest',
    member: 'Member',
    you: 'You',
    addAComment: 'Add a comment…',
    sending: 'Sending…',
    send: 'Send',
  },
};

export default en;
